/**
 * What a person reads when something fails.
 *
 * The API writes its own sentences (`detail`) and those are shown as they come: the back
 * owns the wording of every refusal it makes. What this module owns is everything the
 * API never got to say: the network is down, a proxy answered 502 with an HTML page
 * during a deploy, a response that is not JSON, a bug in the front itself. Before this,
 * those reached the screen as `Failed to fetch`, `Bad Gateway` or a JSON dump, which a
 * person at the shop counter cannot act on.
 */

/** The server could not be reached at all: no answer, not an error answer. */
export const NETWORK_MESSAGE =
  'No se ha podido conectar con el servidor. Comprueba la conexión a internet y vuelve a intentarlo.';

/** Anything the screen cannot explain better. Says what to do, never what broke. */
export const UNEXPECTED_MESSAGE =
  'Algo ha fallado. Vuelve a intentarlo y, si sigue pasando, avisa a quien administra la aplicación.';

/**
 * A sentence for a status the API did not explain (no JSON body, or an empty `detail`).
 *
 * 502 and 503 are what Traefik answers while a container is being replaced, so they say
 * «unos minutos» instead of blaming anything.
 */
export function statusMessage(status: number): string {
  if (status === 0) return NETWORK_MESSAGE;
  if (status === 401) return 'Tu sesión ha caducado. Vuelve a entrar.';
  if (status === 403) return 'No tienes permiso para hacer esto.';
  if (status === 404) return 'No se ha encontrado. Puede que ya no exista.';
  if (status === 408 || status === 504) {
    return 'El servidor ha tardado demasiado en responder. Vuelve a intentarlo en un momento.';
  }
  if (status === 413) return 'El archivo es demasiado grande.';
  if (status === 429) return 'Demasiados intentos seguidos. Espera un momento y vuelve a probar.';
  if (status === 502 || status === 503) {
    return 'El servidor no está disponible ahora mismo. Vuelve a intentarlo en unos minutos.';
  }
  if (status >= 500) {
    return 'Algo ha fallado en el servidor. Vuelve a intentarlo y, si sigue pasando, avisa a quien administra la aplicación.';
  }
  return UNEXPECTED_MESSAGE;
}

/**
 * The reason phrases Starlette writes on its own (`{"detail": "Not Found"}` for a route
 * that does not exist, `Method Not Allowed`...). They are the framework talking, not the
 * API: an older back sends them in English, so they are swapped for `statusMessage`.
 */
const FRAMEWORK_PHRASES: ReadonlySet<string> = new Set([
  'Bad Request',
  'Unauthorized',
  'Not authenticated',
  'Forbidden',
  'Not Found',
  'Method Not Allowed',
  'Request Entity Too Large',
  'Content Too Large',
  'Unsupported Media Type',
  'Unprocessable Entity',
  'Unprocessable Content',
  'Too Many Requests',
  'Internal Server Error',
  'Bad Gateway',
  'Service Unavailable',
  'Gateway Timeout',
]);

/** Error raised by the HTTP client when the API answers with a non 2xx status. */
export class ApiError extends Error {
  /** The HTTP status, or 0 when there was no answer at all (network down, DNS, CORS). */
  readonly status: number;
  /** The sentence to show: the API's own, or `statusMessage` when it sent none. */
  readonly detail: string;
  /**
   * The machine readable reason, from `X-Cubas-Reason`, null when the API sent none. On a
   * 403 (contract v10): `elevation_required` (the administrator has to retype the
   * password) or `password_change_required` (this person has to choose their own password
   * first). Since contract v10.45 (the second factor) also on a 401: `wrong_code`,
   * `wrong_password`, `second_factor_restart` (the ten minutes after the password ran out)
   * and `second_factor_required` (the session was ended: set up the app at the next login);
   * and on a 503, `two_factor_unavailable`.
   */
  readonly reason: string | null;

  constructor(status: number, detail: string, reason: string | null = null) {
    super(`${status}: ${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.reason = reason;
  }

  /** The request never got an answer. */
  static network(): ApiError {
    return new ApiError(0, NETWORK_MESSAGE);
  }

  /** True when the session is missing or expired, which sends the user back to the login. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }

  get needsElevation(): boolean {
    return this.status === 403 && this.reason === 'elevation_required';
  }

  get needsPasswordChange(): boolean {
    return this.status === 403 && this.reason === 'password_change_required';
  }

  static async fromResponse(response: Response): Promise<ApiError> {
    let detail: string | null = null;
    try {
      const body: unknown = await response.json();
      if (body && typeof body === 'object' && 'detail' in body) {
        detail = readDetail(body.detail);
      }
    } catch {
      // A body that is not JSON (a proxy's HTML page) says nothing a person can use.
    }
    if (detail === null || FRAMEWORK_PHRASES.has(detail)) {
      detail = statusMessage(response.status);
    }
    return new ApiError(response.status, detail, response.headers.get('X-Cubas-Reason'));
  }
}

/**
 * An error the front raises on purpose, with a sentence written for a person
 * («Cancelado: hacía falta volver a escribir la contraseña.»). `errorMessage` shows its
 * message; any other `Error` is a bug and is not shown.
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

/**
 * The API's `detail`, as a sentence, or null when it did not send one.
 *
 * A 422 from FastAPI does not send a string: it sends a list of
 * `{loc, msg, type}` from Pydantic, one per field it refused. Printing
 * `JSON.stringify` of that put `[{"type":"decimal_parsing","loc":["body","lines",0,...`
 * in front of the user, which is not a message, it is a stack trace wearing a coat. The
 * `msg` fields are the part written for a person (the back writes them in Spanish), so
 * they are what is shown.
 */
function readDetail(raw: unknown): string | null {
  if (typeof raw === 'string') return raw.trim() || null;
  if (Array.isArray(raw)) {
    const messages = raw
      .map((entry) => {
        if (!entry || typeof entry !== 'object' || !('msg' in entry)) return null;
        const { msg } = entry as { msg: unknown };
        return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
      })
      .filter((message): message is string => message !== null);
    const unique = [...new Set(messages)];
    if (unique.length > 0) return unique.join('. ');
  }
  return null;
}

/**
 * Message to show the user for any thrown value.
 *
 * Only an `ApiError` or a `UserFacingError` carries a sentence meant for a person. Any other `Error` is the
 * browser or the front's own code (`TypeError: Cannot read properties of undefined`),
 * and its message is for Sentry, not for the screen.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof UserFacingError) return error.message;
  return UNEXPECTED_MESSAGE;
}
